import { z } from "zod";
import { isoDateSchema } from "../utils/dates";
import { availabilityWindowSchema } from "./availabilitySchema";
import { agreementSignatureSchema } from "./agreementSchema";
import { idSchema, optionalText } from "./fields";

export const reservationStatusSchema = z.enum(["confirmed", "cancelled"]);
export type ReservationStatus = z.infer<typeof reservationStatusSchema>;

export const reservationSchema = z.object({
  id: idSchema,
  boatId: idSchema,
  boatName: z.string(),
  userId: idSchema,
  userDisplayName: z.string(),
  startDate: isoDateSchema,
  endDate: isoDateSchema,
  partySize: z.number().int().positive(),
  notes: z.string().optional(),
  status: reservationStatusSchema,
  createdAt: z.date(),
  cancelledAt: z.date().optional(),
});
export type Reservation = z.infer<typeof reservationSchema>;

export const newReservationSchema = z
  .object({
    boatId: idSchema,
    startDate: isoDateSchema,
    endDate: isoDateSchema,
    partySize: z
      .number({ error: "Party size must be a number." })
      .int("Party size must be a whole number.")
      .min(1, "Party size must be at least 1."),
    notes: optionalText(1000),
    /** Named in the Boat Loan Agreement as the only people allowed to operate the boat. */
    operatorNames: z
      .string()
      .trim()
      .min(2, "List who will operate the boat.")
      .max(300, "Keep this under 300 characters."),
    /** The Borrower's signature on this trip's Boat Loan Agreement. */
    loanAgreement: agreementSignatureSchema,
  })
  .refine((r) => r.endDate >= r.startDate, {
    message: "End date must be on or after the start date.",
    path: ["endDate"],
  });
export type NewReservation = z.infer<typeof newReservationSchema>;

/** A reservation as shown on the shared calendar; other people's names are admin-only. */
export const calendarReservationSchema = z.object({
  id: idSchema,
  startDate: isoDateSchema,
  endDate: isoDateSchema,
  isMine: z.boolean(),
  reservedBy: z.string().optional(),
});
export type CalendarReservation = z.infer<typeof calendarReservationSchema>;

export const calendarSchema = z.object({
  today: isoDateSchema,
  windows: z.array(availabilityWindowSchema),
  reservations: z.array(calendarReservationSchema),
});
export type Calendar = z.infer<typeof calendarSchema>;

export const calendarQuerySchema = z
  .object({ boatId: idSchema, from: isoDateSchema, to: isoDateSchema })
  .refine((q) => q.to >= q.from, { message: "`to` must not precede `from`." });
